import { useMutation } from '@tanstack/react-query';
import { splitEvery } from 'ramda';
import React, { useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';

import { getErrorContent } from 'common/utils';
import CustomToast from 'modules/bulk-image-upload/components/CustomToast';
import { updateOffer } from 'modules/shared-menu/api';
import useAssetRepositoryUpload from 'modules/shared-menu/hooks/useAssetRepositoryUpload/useAssetRepositoryUpload';
import { cleanFileName } from 'modules/shared-menu/pages/SharedMenuEditorPage/hooks/useProcessSelectionImage';

import { useSelectionData } from '../../../hooks/useSelectionData';
import { FilesMatch, UploadError } from '../types';
import { buildOfferUpdatePayload, getFileNameId } from '../utils/utils';

const UPDATE_BATCH_SIZE = 5;
const TOAST_ID = 'bulk-image-upload-toast';

const incrementUploadProgressToast = (count: number, total: number) => {
  toast.custom(
    <CustomToast variant="loading">
      <FormattedMessage id="menu.bulk-image.upload-progress" values={{ count, total }} />
    </CustomToast>,
    { duration: Infinity, id: TOAST_ID },
  );
};

const updateMatchedOffers = async (filesMatch: FilesMatch, selectionId: string, imagesUrls: string[]) => {
  const offerUpdatePayloads = (filesMatch.matchedOffers || []).map(offer => buildOfferUpdatePayload(offer, imagesUrls));
  await Promise.all(
    offerUpdatePayloads.map(payload => {
      const { id: offerId, ...updatePlayload } = payload;
      return updateOffer(selectionId, offerId, updatePlayload);
    }),
  );
};

const useBulkImageUpload = (
  selectionId: string,
  filesMatches: FilesMatch[],
  errorCallback: (errors: UploadError[]) => void,
) => {
  const { resetDraftSelectionQuery } = useSelectionData();
  const successes = useMemo(() => filesMatches.filter(r => r.status === 'success'), [filesMatches]);
  const { uploadAssets } = useAssetRepositoryUpload();

  const uploadImages = useCallback(
    async (successMatches: FilesMatch[]) => {
      if (!selectionId) {
        throw new Error('No menu id in context! This should not happen.');
      }
      const errors: UploadError[] = [];

      const chunk = splitEvery(UPDATE_BATCH_SIZE, successMatches);

      let cursor = 0;
      const total = successMatches.reduce((totalImages, { files }) => totalImages + files.length, 0);
      for (const chunkItems of chunk) {
        await Promise.all(
          chunkItems.map(async successMatch => {
            try {
              const { publishedImagesUrls } = await uploadAssets(
                successMatch.files.map(file => new File([file], cleanFileName(file.name), { type: file.type })),
              );

              if (publishedImagesUrls.length < 1) {
                throw new Error('None of the images were uploaded');
              }

              await updateMatchedOffers(successMatch, selectionId, publishedImagesUrls);
            } catch (error) {
              errors.push({ fileNameId: getFileNameId(successMatch.files[0].name), error: getErrorContent(error) });
            } finally {
              cursor += successMatch.files.length;
              incrementUploadProgressToast(cursor, total);
            }
          }),
        );
      }
      // Short delay effect for clearer toast feedback
      await new Promise(res => setTimeout(res, 500));

      if (errors.length > 0) {
        errorCallback(errors);
        throw new Error();
      }
    },
    [errorCallback, selectionId, uploadAssets],
  );

  const handleErrorState = useCallback(() => {
    toast.custom(
      <CustomToast variant="error" onCloseButtonClick={() => toast.dismiss(TOAST_ID)}>
        <FormattedMessage id="menu.bulk-image.upload-toast-error" />
      </CustomToast>,
      {
        duration: Infinity,
        id: TOAST_ID,
      },
    );
  }, []);

  const handleSuccessState = useCallback((successCount: number) => {
    toast.custom(
      <CustomToast variant="success" onCloseButtonClick={() => toast.dismiss(TOAST_ID)}>
        <FormattedMessage id="menu.bulk-image.upload-toast-success" values={{ count: successCount }} />
      </CustomToast>,
      {
        duration: Infinity,
        id: TOAST_ID,
      },
    );
  }, []);

  const {
    mutate: handleBulkImageUpload,
    status: uploadStatus,
    reset: resetUploadState,
  } = useMutation(
    async () => {
      if (!selectionId) {
        throw new Error('No menu id in context! This should not happen.');
      }
      toast.custom(<CustomToast variant="loading">...</CustomToast>, { duration: Infinity, id: TOAST_ID });

      await uploadImages(successes);
    },
    {
      onError: () => {
        handleErrorState();
      },
      onSuccess: () => {
        handleSuccessState(successes.reduce((totalImages, { files }) => totalImages + files.length, 0));
      },
      onSettled: () => {
        resetDraftSelectionQuery();
      },
    },
  );

  return { handleBulkImageUpload, uploadStatus, resetUploadState };
};

export default useBulkImageUpload;
